/**
 * Card "automazione" nella cronologia: cosa ha fatto un workflow su questo
 * contatto, passo per passo.
 *
 * Stessa anatomia delle altre card (colonna immagine, titolo, riga in basso
 * con la tipologia) e, per ogni step, non solo il nome ma quello che e' stato
 * fatto davvero: a chi e' andata l'email e con che oggetto, il testo del
 * WhatsApp, da quale fase a quale fase e' passato il lead, chi e' stato
 * assegnato. Il motore scrive questi dettagli in `metadata.steps[].detail` e
 * `preview`; le esecuzioni vecchie, senza dettaglio, mostrano solo il nome.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, XCircle, Clock, Loader2, Sparkles, Mail, MessageCircle, ExternalLink } from 'lucide-react';
import { CardMedia } from '../shared/timeline/CardMedia';
import { TimelineFooter } from '../shared/timeline/TimelineFooter';
import { mediaTileCls } from '../shared/timeline/cardStyles';

interface WorkflowStep {
  id: string;
  name: string;
  type: string;
  status: 'pending' | 'running' | 'success' | 'failed' | 'waiting';
  executed_at?: string;
  step_order: number;
  /** Frase in chiaro su cosa e' stato fatto: "Email a mario@… · Oggetto: …". */
  detail?: string;
  /** Contenuto inviato (testo WhatsApp, corpo email), mostrato a richiesta. */
  preview?: string;
  preview_kind?: 'email' | 'whatsapp' | 'text';
  error?: string;
}

interface WorkflowExecutionMetadata {
  source: 'workflow_execution';
  workflow_id: string;
  workflow_name: string;
  execution_id: string;
  status: 'running' | 'completed' | 'failed' | 'waiting';
  started_at: string;
  completed_at?: string;
  steps: WorkflowStep[];
}

interface WorkflowNoteCardProps {
  note: {
    id: string;
    content: string;
    metadata: WorkflowExecutionMetadata;
    created_at: string;
    updated_at?: string;
  };
  formatDate?: (dateString: string) => string;
}

const STATUS_BADGE: Record<string, { label: string; class: string }> = {
  running: { label: 'In corso', class: 'bg-[#2383E2]/10 text-[#2383E2]' },
  waiting: { label: 'In attesa', class: 'bg-[#DFAB01]/10 text-[#DFAB01]' },
  completed: { label: 'Completata', class: 'bg-[#0F7B6C]/10 text-[#0F7B6C]' },
  failed: { label: 'Errore', class: 'bg-[#EB5757]/10 text-[#EB5757]' },
};

const STEP_STATUS_ICON: Record<string, React.ReactNode> = {
  success: <CheckCircle2 className="h-3.5 w-3.5 text-[#0F7B6C]" />,
  failed: <XCircle className="h-3.5 w-3.5 text-[#EB5757]" />,
  waiting: <Clock className="h-3.5 w-3.5 text-[#DFAB01]" />,
  running: <Loader2 className="h-3.5 w-3.5 animate-spin text-[#2383E2]" />,
  pending: <span className="block h-2 w-2 rounded-full border border-[#D3D3D0] dark:border-[#5A5A58]" />,
};

/** Un passo: nome, dettaglio in chiaro e, se c'e', il contenuto inviato. */
function Passo({ step }: { step: WorkflowStep }) {
  const [aperto, setAperto] = useState(false);
  const pendente = step.status === 'pending';
  const Icona = step.preview_kind === 'email' ? Mail : step.preview_kind === 'whatsapp' ? MessageCircle : null;

  return (
    <div className="flex gap-2 py-1">
      <div className="mt-[3px] flex w-3.5 flex-shrink-0 justify-center">
        {STEP_STATUS_ICON[step.status] || STEP_STATUS_ICON.pending}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span
            className={`text-[12px] ${
              step.status === 'failed'
                ? 'font-medium text-[#EB5757]'
                : pendente
                  ? 'text-[#B9B9B7] dark:text-white/30'
                  : 'text-[#37352F] dark:text-white'
            }`}
          >
            {step.name}
          </span>
          {step.executed_at && (
            <span className="text-[10px] text-[#B9B9B7] dark:text-white/30">
              {new Date(step.executed_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </div>
        {step.detail && !pendente && (
          <div className="mt-0.5 text-[11px] leading-snug text-[#787774] dark:text-white/50">{step.detail}</div>
        )}
        {step.error && (
          <div className="mt-0.5 text-[11px] leading-snug text-[#EB5757]">{step.error}</div>
        )}
        {step.preview && (
          <div className="mt-1">
            <button
              type="button"
              onClick={() => setAperto((v) => !v)}
              className="inline-flex items-center gap-1 text-[10px] text-[#2383E2] hover:underline"
            >
              {Icona && <Icona className="h-3 w-3" />}
              {aperto ? 'nascondi il messaggio' : step.preview_kind === 'email' ? "vedi l'email" : 'vedi il messaggio'}
            </button>
            {aperto && (
              <div className="mt-1 max-h-48 overflow-y-auto whitespace-pre-line rounded-md border border-[rgba(55,53,47,0.09)] bg-[#FBFBFA] px-2.5 py-2 text-[11px] leading-relaxed text-[#37352F] dark:border-white/[0.08] dark:bg-white/[0.03] dark:text-gray-300">
                {step.preview}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function WorkflowNoteCard({ note, formatDate }: WorkflowNoteCardProps) {
  const metadata = note.metadata;
  const status = metadata?.status || 'running';
  const badge = STATUS_BADGE[status] || STATUS_BADGE.running;

  // Aperta finche' gira, chiusa quando ha finito: il riassunto sotto il titolo basta.
  // Gli hook stanno tutti prima dell'uscita anticipata, per le regole degli hook.
  const [expanded, setExpanded] = useState(status === 'running' || status === 'waiting');
  const [tuttiIPassi, setTuttiIPassi] = useState(false);
  if (!metadata?.source || metadata.source !== 'workflow_execution') return null;

  const steps = (metadata.steps || []).slice().sort((a, b) => a.step_order - b.step_order);
  const fatti = steps.filter((s) => s.status === 'success');
  const totale = steps.length;
  const fallito = steps.find((s) => s.status === 'failed');
  // Riassunto da chiusa: i dettagli dei passi eseguiti, in fila; se un passo e'
  // fallito, l'errore viene prima di tutto.
  const riassunto = [
    fallito ? `Errore in "${fallito.name}"${fallito.error ? `: ${fallito.error}` : ''}` : '',
    ...fatti.map((s) => s.detail || s.name),
  ].filter(Boolean).join(' · ');
  const data = workflowNoteDate(note);

  // Un workflow a goccia puo' avere cinquanta passi: da aperta mostra quelli
  // eseguiti e il prossimo, i futuri restano dietro un "e altri N".
  const ultimoFatto = steps.reduce((m, s, i) => (s.status !== 'pending' ? i : m), -1);
  const visibili = tuttiIPassi ? steps : steps.slice(0, Math.min(steps.length, ultimoFatto + 2));
  const nascosti = steps.length - visibili.length;

  return (
    <div className="group relative mb-3 rounded-lg border border-[rgba(55,53,47,0.16)] bg-white transition-colors hover:border-[rgba(55,53,47,0.26)] dark:border-[#3F4447] dark:bg-[#2F3437] dark:hover:border-[#565D61]">
      <div className="min-w-0 p-3">
        <button onClick={() => setExpanded((v) => !v)} aria-expanded={expanded} className="block w-full text-left">
          <CardMedia
            media={
              <span className={mediaTileCls('flusia')}>
                <Sparkles size={15} strokeWidth={1.5} />
              </span>
            }
          >
            <div className="flex items-center gap-2">
              <span className="truncate text-sm font-medium text-[#37352F] dark:text-white">
                {metadata.workflow_name || 'Automazione'}
              </span>
              <span className={`inline-flex flex-shrink-0 items-center rounded px-1.5 py-0.5 text-[9px] font-semibold ${badge.class}`}>
                {badge.label}
              </span>
            </div>
            {!expanded && (
              <div className="mt-0.5 line-clamp-2 text-xs text-[#787774] dark:text-white/50">
                {riassunto || `${fatti.length} passi su ${totale}`}
              </div>
            )}
          </CardMedia>
        </button>

        {expanded && (
          <div className="mt-2 border-t border-[rgba(55,53,47,0.06)] pl-11 pt-1.5 dark:border-white/[0.06]">
            {visibili.map((step) => (
              <Passo key={step.id} step={step} />
            ))}
            {nascosti > 0 && (
              <button
                type="button"
                onClick={() => setTuttiIPassi((v) => !v)}
                className="mt-1 pl-[22px] text-[11px] text-[#2383E2] hover:underline"
              >
                {tuttiIPassi ? 'nascondi i passi futuri' : `e altri ${nascosti} passi ancora da eseguire`}
              </button>
            )}
          </div>
        )}

        <TimelineFooter
          tipo="Automazione"
          accent="flusia"
          date={data}
          titoloData={formatDate ? formatDate(data) : undefined}
          expanded={expanded}
          left={
            <>
              <span className="text-[10px] text-[#9B9A97] dark:text-white/40">
                {fatti.length}/{totale} passi
              </span>
              {metadata.workflow_id && (
                <Link
                  to={`/automation?workflow=${metadata.workflow_id}`}
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-0.5 text-[10px] text-[#2383E2] hover:underline"
                  title="Apri l'automazione"
                >
                  Apri l'automazione <ExternalLink className="h-2.5 w-2.5" />
                </Link>
              )}
            </>
          }
        />
      </div>
    </div>
  );
}

// Helper to check if a note is a workflow note
export function isWorkflowNote(note: any): boolean {
  return note?.metadata?.source === 'workflow_execution';
}

/**
 * Data con cui la ricevuta sta in cronologia: l'ultimo passo eseguito.
 * Un'automazione con attese dura giorni; alla data di avvio sprofonderebbe.
 */
export function workflowNoteDate(note: any): string {
  if (!isWorkflowNote(note)) return note.created_at;
  const m = note.metadata || {};
  const ultimo = (m.steps || [])
    .map((s: any) => s?.executed_at)
    .filter(Boolean)
    .sort()
    .pop();
  return m.completed_at || ultimo || note.updated_at || note.created_at;
}
